import { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Chip,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Badge,
} from "@mui/material";
import {
  KeyboardArrowDown,
  MenuBook,
  CheckCircle,
  Schedule,
  Dashboard,
  Add,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns/formatDistanceToNow";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import { setBaseView } from "@store/slices/viewSlice";
import {
  selectReading,
  selectCanvas,
} from "@store/thunks/workspaceThunks";
import {
  selectCurrentReading,
  selectCurrentCanvas,
  selectSelectedReadingId,
  selectSelectedCanvasId,
  selectIsReadingDirty,
  selectIsCanvasDirty,
  selectReadings,
  selectCanvases,
  selectIsCurrentUserWorkspaceOwner,
} from "@store/selectors";
import { selectBaseView } from "@store/slices/viewSlice";
import { CreateReadingModal } from "@features/workspace-readings/components/CreateReadingModal";
import { CreateCanvasModal } from "@features/annogram/resources/components/CreateCanvasModal";
import { TOUR } from "@features/onboarding/tourTargets";

/**
 * ResourceSelector - Contextual dropdown that shows resources for the active base view
 * - When in Reading mode: Shows list of available readings with rich metadata
 * - When in Canvas mode: Shows list of available canvases
 * - Automatically hides when no resources are available
 */
export default function ResourceSelector() {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createReadingOpen, setCreateReadingOpen] = useState(false);
  const [createCanvasOpen, setCreateCanvasOpen] = useState(false);

  const baseView = useAppSelector(selectBaseView);
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const readings = useAppSelector(selectReadings);
  const canvases = useAppSelector(selectCanvases);
  const currentReading = useAppSelector(selectCurrentReading);
  const currentCanvas = useAppSelector(selectCurrentCanvas);
  const isReadingDirty = useAppSelector(selectIsReadingDirty);
  const isCanvasDirty = useAppSelector(selectIsCanvasDirty);
  // Resource creation is an owner capability (matches the API, which gates
  // POST reading/canvas/paper on workspace ownership, not tier).
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const workspaceId = useAppSelector(
    (state) => state.workspace.workspace_id
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectReading = (readingId: string) => {
    if (readingId !== selectedReadingId) {
      // Use thunk for auto-save before switching
      dispatch(selectReading(readingId));
      // Auto-switch to reading view if not already there
      if (baseView !== "reading") {
        dispatch(setBaseView("reading"));
      }
    }
    handleClose();
  };

  const handleSelectCanvas = (canvasId: string) => {
    if (canvasId !== selectedCanvasId) {
      // Use thunk for auto-save before switching
      dispatch(selectCanvas(canvasId));
      // Auto-switch to canvas view if not already there
      if (baseView !== "canvas") {
        dispatch(setBaseView("canvas"));
      }
    }
    handleClose();
  };

  const getStatusColor = (
    status: string
  ):
    | "default"
    | "success"
    | "warning"
    | "error"
    | "info"
    | "primary"
    | "secondary" => {
    switch (status) {
      case "completed":
      case "submitted":
        return "success";
      case "in_progress":
        return "warning";
      case "assigned":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
      case "submitted":
        return <CheckCircle fontSize="small" />;
      case "in_progress":
        return <Schedule fontSize="small" />;
      default:
        return null;
    }
  };

  // Show reading selector when in reading mode
  if (baseView === "reading") {
    // Show empty state if no readings available
    if (readings.length === 0) {
      return (
        <>
          <Button
            data-tour={TOUR.resourceSelector}
            startIcon={<MenuBook />}
            onClick={() => setCreateReadingOpen(true)}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
            }}
            aria-label="No readings available, click to add one"
          >
            No readings - Add one
          </Button>
          {workspaceId && (
            <CreateReadingModal
              open={createReadingOpen}
              onClose={() => setCreateReadingOpen(false)}
              workspaceId={workspaceId}
            />
          )}
        </>
      );
    }

    // Show loading state if no current reading
    if (!currentReading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading reading...
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Badge
          color="warning"
          variant="dot"
          invisible={!isReadingDirty}
          sx={{ "& .MuiBadge-badge": { right: 8, top: 8 } }}
        >
          <Button
            data-tour={TOUR.resourceSelector}
            onClick={handleClick}
            startIcon={<MenuBook />}
            endIcon={<KeyboardArrowDown />}
            sx={{
              textTransform: "none",
              color: "text.primary",
              fontWeight: 500,
              maxWidth: 300,
            }}
            title={
              isReadingDirty ? "You have unsaved changes" : undefined
            }
          >
            <Box sx={{ textAlign: "left", overflow: "hidden" }}>
              <Typography variant="body2" noWrap>
                {currentReading.reading_name || currentReading.document_name}
              </Typography>
              {currentReading.required && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Required Reading
                </Typography>
              )}
            </Box>
          </Button>
        </Badge>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: { minWidth: 350, maxHeight: 500 },
            },
          }}
        >
          {/* Current reading header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
            <Typography variant="caption" color="text.secondary">
              Current Reading
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
              {currentReading.reading_name || currentReading.document_name}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {currentReading.status && (
                <Chip
                  size="small"
                  label={currentReading.status.replace("_", " ")}
                  color={getStatusColor(currentReading.status)}
                  icon={getStatusIcon(currentReading.status) || undefined}
                />
              )}
              {currentReading.annotation_count > 0 && (
                <Chip
                  size="small"
                  label={`${currentReading.annotation_count} highlights`}
                  variant="outlined"
                />
              )}
            </Box>
            {currentReading.due_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Due{" "}
                {formatDistanceToNow(new Date(currentReading.due_date), {
                  addSuffix: true,
                })}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Other readings */}
          <MenuItem disabled>
            <Typography variant="caption">Switch Reading</Typography>
          </MenuItem>

          {readings
            .filter((r) => r.reading_id !== currentReading.reading_id)
            .map((reading) => (
              <MenuItem
                key={reading.reading_id}
                onClick={() => handleSelectReading(reading.reading_id)}
              >
                <ListItemText
                  primary={reading.reading_name || reading.document_name}
                  secondary={
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mt: 0.5,
                      }}
                    >
                      {reading.status && (
                        <Chip
                          size="small"
                          label={reading.status.replace("_", " ")}
                          color={getStatusColor(reading.status)}
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                      {reading.due_date && (
                        <Typography variant="caption">
                          Due{" "}
                          {formatDistanceToNow(new Date(reading.due_date), {
                            addSuffix: true,
                          })}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </MenuItem>
            ))}

          {readings.length === 1 && (
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                No other readings in this workspace
              </Typography>
            </MenuItem>
          )}

          {isOwner && [
            <Divider key="divider" />,
            <MenuItem
              key="add-reading"
              onClick={() => {
                handleClose();
                setCreateReadingOpen(true);
              }}
            >
              <Add fontSize="small" sx={{ mr: 1 }} />
              Add Reading
            </MenuItem>,
          ]}
        </Menu>

        {workspaceId && (
          <CreateReadingModal
            open={createReadingOpen}
            onClose={() => setCreateReadingOpen(false)}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  }

  // Show canvas selector when in canvas mode
  if (baseView === "canvas") {
    // Show empty state if no canvases available
    if (canvases.length === 0) {
      return (
        <>
          <Button
            data-tour={TOUR.resourceSelector}
            startIcon={<Dashboard />}
            onClick={() => setCreateCanvasOpen(true)}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
            }}
            aria-label="No canvases available, click to add one"
          >
            No canvases - Add one
          </Button>
          {workspaceId && (
            <CreateCanvasModal
              open={createCanvasOpen}
              onClose={() => setCreateCanvasOpen(false)}
              workspaceId={workspaceId}
            />
          )}
        </>
      );
    }

    // Show loading state if no current canvas
    if (!currentCanvas) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading canvas...
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Badge
          color="warning"
          variant="dot"
          invisible={!isCanvasDirty}
          sx={{ "& .MuiBadge-badge": { right: 8, top: 8 } }}
        >
          <Button
            data-tour={TOUR.resourceSelector}
            onClick={handleClick}
            startIcon={<Dashboard />}
            endIcon={<KeyboardArrowDown />}
            sx={{
              textTransform: "none",
              color: "text.primary",
              fontWeight: 500,
              maxWidth: 300,
            }}
            title={isCanvasDirty ? "You have unsaved changes" : undefined}
          >
            <Box sx={{ textAlign: "left", overflow: "hidden" }}>
              <Typography variant="body2" noWrap>
                {currentCanvas.canvas_name || "Untitled Canvas"}
              </Typography>
            </Box>
          </Button>
        </Badge>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: { minWidth: 350, maxHeight: 500 },
            },
          }}
        >
          {/* Current canvas header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
            <Typography variant="caption" color="text.secondary">
              Current Canvas
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
              {currentCanvas.canvas_name || "Untitled Canvas"}
            </Typography>
            {currentCanvas.due_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Due{" "}
                {formatDistanceToNow(new Date(currentCanvas.due_date), {
                  addSuffix: true,
                })}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Other canvases */}
          <MenuItem disabled>
            <Typography variant="caption">Switch Canvas</Typography>
          </MenuItem>

          {canvases
            .filter((c) => c.canvas_id !== currentCanvas.canvas_id)
            .map((canvas) => (
              <MenuItem
                key={canvas.canvas_id}
                onClick={() => handleSelectCanvas(canvas.canvas_id)}
              >
                <ListItemText
                  primary={canvas.canvas_name || "Untitled Canvas"}
                  secondary={
                    canvas.due_date
                      ? `Due ${formatDistanceToNow(new Date(canvas.due_date), {
                          addSuffix: true,
                        })}`
                      : undefined
                  }
                />
              </MenuItem>
            ))}

          {canvases.length === 1 && (
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                No other canvases in this workspace
              </Typography>
            </MenuItem>
          )}

          {isOwner && [
            <Divider key="divider" />,
            <MenuItem
              key="add-canvas"
              onClick={() => {
                handleClose();
                setCreateCanvasOpen(true);
              }}
            >
              <Add fontSize="small" sx={{ mr: 1 }} />
              Add Canvas
            </MenuItem>,
          ]}
        </Menu>

        {workspaceId && (
          <CreateCanvasModal
            open={createCanvasOpen}
            onClose={() => setCreateCanvasOpen(false)}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  }

  return null;
}
